import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/feature/auth';
import { reviewService } from '../review/service/review.service';
import { teamService, ITeam } from '../team/service/team.service';

import { UpgradeModal } from '../common/components/upgrade-modal.component';
import { CreateQueueModal, CreateQueueFormData } from '../common/components/create-queue-modal.component';
import { useDashboardSocket } from './hooks/use-dashboard-socket';
import { DiscordPill, WebhookPill } from '../common/components/discord-pill.component';

export const DashboardPage = () => {
  const { user, checkAuth } = useAuth();
  useDashboardSocket(user?.id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showProSuccess, setShowProSuccess] = React.useState(() => searchParams.get('checkout') === 'success');

  const handleDismissProSuccess = React.useCallback(async () => {
    setShowProSuccess(false);
    await checkAuth();
    navigate('/dashboard', { replace: true });
  }, [checkAuth, navigate]);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState<string | null>(null);

  const { data: reviews, isLoading } = useQuery({
    queryKey: ['admin-reviews'],
    queryFn: () => reviewService.getAdminReviews(),
    refetchOnMount: true,
    refetchInterval: false,
    staleTime: 0
  });

  const { data: teams } = useQuery({
    queryKey: ['my-teams'],
    queryFn: () => teamService.getMyTeams(),
    staleTime: 0
  });

  // Fetch active reviews for all teams the user is in
  const { data: teamReviews } = useQuery({
    queryKey: ['team-active-reviews'],
    queryFn: async () => {
      if (!teams || !(teams as ITeam[]).length) return [];
      const results = await Promise.all(
        (teams as ITeam[]).map(async (team) => {
          const revs = await teamService.getTeamReviews(team.id, true);
          return ((revs as any[]) || []).map((r: any) => ({ ...r, teamName: team.name }));
        })
      );
      return results.flat();
    },
    enabled: !!(teams && (teams as ITeam[]).length),
    staleTime: 0
  });

  // Fetch active reviews user has joined as a participant
  const { data: participatingReviews } = useQuery({
    queryKey: ['my-participating-reviews'],
    queryFn: () => reviewService.getMyParticipatingReviews(),
    staleTime: 0
  });

  const { mutate: createReview, isPending: createPending } = useMutation({
    mutationFn: (data: { title: string; meetingUrl?: string; teamIds?: number[]; password?: string }) =>
      reviewService.createReview(data.title, data.meetingUrl, data.teamIds, data.password),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
        queryClient.invalidateQueries({ queryKey: ['team-active-reviews'] });
        setShowCreateModal(false);
        navigate(`/review/${data.slug}/admin`);
      }
    },
    onError: (err: any) => {
      const messages = err.response?.data?.message;
      const detail = Array.isArray(messages) ? messages[0] : messages;
      if (err.response?.status === 403 && detail) {
        setUpgradeMessage(detail);
      } else {
        toast.error(detail || t('dashboard.createReviewFailed'));
      }
    }
  });

  const handleCreateSubmit = (data: CreateQueueFormData) => {
    createReview(data);
  };

  // Separate personal reviews from team reviews the user created
  const myReviews = (reviews as any[]) || [];
  const activeTeamReviews = ((teamReviews as any[]) || []).filter(
    (r: any) => !myReviews.some((mr: any) => mr.id === r.id)
  );
  const joinedReviews = ((participatingReviews as any[]) || []).filter(
    (r: any) =>
      !myReviews.some((mr: any) => mr.id === r.id) &&
      !activeTeamReviews.some((tr: any) => tr.id === r.id)
  );

  // Expiring reviews notification
  const { data: expiringReviews } = useQuery({
    queryKey: ['expiring-reviews'],
    queryFn: () => reviewService.getExpiringReviews(),
    staleTime: 0
  });

  const { mutate: extendReview } = useMutation({
    mutationFn: (slug: string) => reviewService.extendReview(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expiring-reviews'] });
      toast.success(t('dashboard.reviewExtended'));
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      toast.error(msg || t('dashboard.extendFailed'));
    }
  });

  const expiringList = (expiringReviews as any[]) || [];

  return (
    <div className="max-w-[800px] mx-auto py-[40px] px-[16px]">
      <h1 className="font-display text-[28px] font-bold text-base-black dark:text-white mb-[8px]">
        {t('dashboard.title')}
      </h1>
      <p className="font-body text-[16px] text-gray-900 dark:text-[#A0A0A3] mb-[32px]">
        {t('dashboard.welcomeBack', { name: user?.displayName || user?.email?.split('@')[0] })}
      </p>

      {expiringList.length > 0 && (
        <div className="mb-[24px] p-[16px] rounded-[16px] bg-[#FFFBEB] dark:bg-[#3D2E0A] border border-[#FDE68A] dark:border-[#92400E]/40 shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)]">
          <h3 className="font-body text-[14px] font-semibold text-[#92400E] dark:text-[#FDE68A] mb-[12px]">
            {t('dashboard.expiringReviews')}
          </h3>
          <div className="flex flex-col gap-[8px]">
            {expiringList.map((r: any) => {
              const daysLeft = Math.max(0, Math.ceil((new Date(r.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
              return (
                <div key={r.id} className="flex items-center justify-between gap-[8px] p-[10px_12px] rounded-[10px] bg-white dark:bg-[#2A2310] border border-[#FDE68A] dark:border-[#92400E]/30">
                  <div className="min-w-0">
                    <p className="font-body text-[14px] font-semibold text-base-black dark:text-white truncate">
                      {r.title}
                    </p>
                    <p className="font-body text-[12px] text-[#92400E] dark:text-[#FDE68A]">
                      {daysLeft === 0 ? t('dashboard.expiresToday') : `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                      {r.extendedOnce && ' (already extended)'}
                    </p>
                  </div>
                  <div className="flex gap-[6px] flex-shrink-0">
                    {!r.extendedOnce && (
                      <button
                        type="button"
                        onClick={() => extendReview(r.slug)}
                        className="h-[32px] px-[12px] rounded-[32px] bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[12px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
                      >
                        {t('dashboard.extendDays')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/review/${r.slug}/admin`)}
                      className="h-[32px] px-[12px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[12px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
                    >
                      {t('common.view')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] mb-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)]">
        <div className="mb-[16px]">
          <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[12px]">
            {t('dashboard.reviewQueues')}
          </h2>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="h-[36px] px-[16px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
          >
            {t('dashboard.createReview')}
          </button>
        </div>

        {isLoading ? (
          <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">{t('common.loading')}</p>
        ) : myReviews.length > 0 ? (
          <div className="flex flex-col gap-[8px]">
            {myReviews.map((review: any) => (
              <div
                key={review.id}
                className="flex flex-col gap-[4px] p-[12px_16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-[#F5F5F6] dark:bg-[#28282B] hover:shadow-[0_2px_10px_rgba(42,60,68,0.08)] transition-all cursor-pointer md:flex-row md:items-center md:justify-between"
                onClick={() => navigate(`/review/${review.slug}/admin`)}
              >
                <div className="min-w-0">
                  <p className="font-body text-[15px] font-semibold text-[#111827] dark:text-white truncate">
                    {review.title}
                  </p>
                </div>
                <div className="flex items-center gap-[6px] flex-wrap flex-shrink-0">
                  <span className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3]">
                    {review._count?.participants || 0} {t('common.participants')}
                    {review.isActive ? '' : ` (${t('common.ended')})`}
                  </span>
                  {review.discordWebhookUrl && (
                    <DiscordPill channelUrl={review.discordChannelUrl} variant="icon" />
                  )}
                  {review.slackWebhookUrl && (
                    <WebhookPill provider="slack" channelUrl={review.slackChannelUrl} variant="icon" />
                  )}
                  {review.isActive && (
                    <span className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[32px] text-[11px] font-semibold ${
                      review.meetingStarted
                        ? 'bg-green-500 text-green-900 border border-green-900/30 animate-pulseBorder'
                        : 'bg-[#DFDFE2] text-[#58585C] border border-[rgba(0,0,0,0.10)]'
                    }`}>
                      <span className={`w-[6px] h-[6px] rounded-full flex-shrink-0 ${
                        review.meetingStarted ? 'bg-green-900 animate-pulseStrong' : 'bg-[#97979B]'
                      }`} />
                      {review.meetingStarted ? t('meetingStatus.inProgress') : t('meetingStatus.waiting')}
                    </span>
                  )}
                  <span className="font-mono text-[12px] text-gray-700 dark:text-[#6B6B6F]">
                    /{review.slug}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3]">
            {t('dashboard.noReviews')}
          </p>
        )}
      </div>

      {activeTeamReviews.length > 0 && (
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] mb-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)]">
          <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
            {t('dashboard.activeTeamReviews')}
          </h2>
          <div className="flex flex-col gap-[8px]">
            {activeTeamReviews.map((review: any) => (
              <div
                key={review.id}
                className="flex flex-col gap-[4px] p-[12px_16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-[#F5F5F6] dark:bg-[#28282B] hover:shadow-[0_2px_10px_rgba(42,60,68,0.08)] transition-all cursor-pointer md:flex-row md:items-center md:justify-between"
                onClick={() => navigate(`/review/${review.slug}`)}
              >
                <div className="min-w-0">
                  <p className="font-body text-[15px] font-semibold text-[#111827] dark:text-white truncate">
                    {review.title}
                  </p>
                </div>
                <div className="flex items-center gap-[6px] flex-wrap flex-shrink-0">
                  <span className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3]">
                    {review.teamName} &middot; {review._count?.participants || 0} participants
                  </span>
                  {review.discordWebhookUrl && (
                    <DiscordPill channelUrl={review.discordChannelUrl} variant="icon" />
                  )}
                  {review.slackWebhookUrl && (
                    <WebhookPill provider="slack" channelUrl={review.slackChannelUrl} variant="icon" />
                  )}
                  <span className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[32px] text-[11px] font-semibold ${
                    review.meetingStarted
                      ? 'bg-green-500 text-green-900 border border-green-900/30 animate-pulseBorder'
                      : 'bg-[#DFDFE2] text-[#58585C] border border-[rgba(0,0,0,0.10)]'
                  }`}>
                    <span className={`w-[6px] h-[6px] rounded-full flex-shrink-0 ${
                      review.meetingStarted ? 'bg-green-900 animate-pulseStrong' : 'bg-[#97979B]'
                    }`} />
                    {review.meetingStarted ? t('meetingStatus.inProgress') : t('meetingStatus.waiting')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {joinedReviews.length > 0 && (
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] mb-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)]">
          <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[16px]">
            {t('dashboard.joinedReviews')}
          </h2>
          <div className="flex flex-col gap-[8px]">
            {joinedReviews.map((review: any) => (
              <div
                key={review.id}
                className="flex flex-col gap-[4px] p-[12px_16px] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-[#F5F5F6] dark:bg-[#28282B] hover:shadow-[0_2px_10px_rgba(42,60,68,0.08)] transition-all cursor-pointer md:flex-row md:items-center md:justify-between"
                onClick={() => navigate(`/review/${review.slug}`)}
              >
                <div className="min-w-0">
                  <p className="font-body text-[15px] font-semibold text-[#111827] dark:text-white truncate">
                    {review.title}
                  </p>
                </div>
                <div className="flex items-center gap-[6px] flex-wrap flex-shrink-0">
                  <span className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3]">
                    {review._count?.participants || 0} participants
                  </span>
                  {review.discordWebhookUrl && (
                    <DiscordPill channelUrl={review.discordChannelUrl} variant="icon" />
                  )}
                  {review.slackWebhookUrl && (
                    <WebhookPill provider="slack" channelUrl={review.slackChannelUrl} variant="icon" />
                  )}
                  <span className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[32px] text-[11px] font-semibold ${
                    review.meetingStarted
                      ? 'bg-green-500 text-green-900 border border-green-900/30 animate-pulseBorder'
                      : 'bg-[#DFDFE2] text-[#58585C] border border-[rgba(0,0,0,0.10)]'
                  }`}>
                    <span className={`w-[6px] h-[6px] rounded-full flex-shrink-0 ${
                      review.meetingStarted ? 'bg-green-900 animate-pulseStrong' : 'bg-[#97979B]'
                    }`} />
                    {review.meetingStarted ? t('meetingStatus.inProgress') : t('meetingStatus.waiting')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CreateQueueModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        isPending={createPending}
        teams={teams ? (teams as ITeam[]) : []}
        hasPro={!!(user?.isPro || user?.isProGifted || user?.subscriptionTier === 'trial')}
        onUpgrade={(msg) => setUpgradeMessage(msg)}
      />

      {upgradeMessage && (
        <UpgradeModal message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />
      )}

      {showProSuccess && (
        <ProSuccessModal onDismiss={handleDismissProSuccess} />
      )}
    </div>
  );
};

// --- Pro Success Modal ---

const ProSuccessModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const features = [
    'Unlimited active queues & participants',
    'Unlimited teams & co-admins',
    'Discord & Slack webhook notifications',
    'QR code scan-to-join',
    'Password-protected queues',
    'Multi-team support',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]">
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[20px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[420px] p-[32px] flex flex-col items-center text-center">
        {/* Icon */}
        <div className="w-[56px] h-[56px] rounded-full bg-[#1D1D1E] flex items-center justify-center mb-[20px]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white mb-[8px]">
          You're now Pro!
        </h2>
        <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3] mb-[24px]">
          Your subscription is active. Here's what you've unlocked:
        </p>

        <ul className="w-full flex flex-col gap-[8px] mb-[28px]">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-[10px] text-left">
              <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-[#F0F9FF] flex items-center justify-center">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="font-body text-[13px] text-base-black dark:text-white">{f}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onDismiss}
          className="w-full h-[44px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
        >
          Start exploring
        </button>
      </div>
    </div>
  );
};

